import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import {
  AngularFirestore,
  AngularFirestoreCollection,
} from "@angular/fire/firestore";
import { AngularFireFunctions } from "@angular/fire/functions";
import { environment } from "../../environments/environment";
import { User } from "../@core/data/users";
import { UserService } from "../@core/services/user.service";
import { ExerciseUser } from "../models/exercise.model";

@Injectable({
  providedIn: "root",
})
export class ExerciseUserService {
  visibility = environment.visibility;
  baseUrl = environment.apiUrl;
  functionsUrl = environment.functionsUrl;
  userLogin: User;
  private dbPath = "/exercise-users";
  exercisesRef: AngularFirestoreCollection<ExerciseUser>;

  constructor(
    private http: HttpClient,
    private db: AngularFirestore,
    private userStore: UserService,
    private fns: AngularFireFunctions
  ) {
    this.exercisesRef = db.collection(this.dbPath);
    this.userStore.getCurrentUser().subscribe((user: User) => {
      this.userLogin = user;
    });
  }

  //Exercises services
  getTopHighestExerciseUsers(
    exerciseId: string,
    limit: number
  ): AngularFirestoreCollection<ExerciseUser> {
    return this.db.collection(this.dbPath, (ref) =>
      ref
        .where("isDeleted", "==", false)
        .where("isActive", "==", true)
        .where("finished", "==", true)
        .where("isHighest", "==", true)
        .where("exerciseId", "==", exerciseId)
        .orderBy("percentage", "desc")
        .limit(limit)
    );
  }

  getExerciseUsers(
    uid: string,
    exerciseId: string
  ): AngularFirestoreCollection<ExerciseUser> {
    const userId = this.userLogin ? this.userLogin.uid : uid;

    return this.db.collection(this.dbPath, (ref) =>
      ref
        .where("isDeleted", "==", false)
        .where("isActive", "==", true)
        .where("createdBy", "==", userId)
        .where("exerciseId", "==", exerciseId)
        .orderBy("createdOn", "desc")
    );
  }

  getDoingExerciseUsers(
    uid: string,
    exerciseId: string
  ): AngularFirestoreCollection<ExerciseUser> {
    const userId = this.userLogin ? this.userLogin.uid : uid;

    return this.db.collection(this.dbPath, (ref) =>
      ref
        .where("isDeleted", "==", false)
        .where("isActive", "==", true)
        .where("finished", "==", false)
        .where("createdBy", "==", userId)
        .where("exerciseId", "==", exerciseId)
        .orderBy("createdOn", "desc")
        .limit(1)
    );
  }

  getFinishedExerciseUsers(
    uid: string,
    exerciseId: string
  ): AngularFirestoreCollection<ExerciseUser> {
    const userId = this.userLogin ? this.userLogin.uid : uid;

    return this.db.collection(this.dbPath, (ref) =>
      ref
        .where("isDeleted", "==", false)
        .where("isActive", "==", true)
        .where("finished", "==", true)
        .where("createdBy", "==", userId)
        .where("exerciseId", "==", exerciseId)
        .orderBy("createdOn", "desc")
        .limit(50)
    );
  }

  getHighestExerciseUser(
    uid: string,
    exerciseId: string
  ): AngularFirestoreCollection<ExerciseUser> {
    const userId = this.userLogin ? this.userLogin.uid : uid;

    return this.db.collection(this.dbPath, (ref) =>
      ref
        .where("isDeleted", "==", false)
        .where("isActive", "==", true)
        .where("finished", "==", true)
        .where("isHighest", "==", true)
        .where("createdBy", "==", userId)
        .where("exerciseId", "==", exerciseId)
        .orderBy("percentage", "desc")
        .limit(1)
    );
  }

  getExerciseUser(id: string) {
    return this.exercisesRef.doc(id).valueChanges();
  }

  createExercise(exercise: ExerciseUser): any {
    exercise.finished = false;
    exercise.noAttempt = exercise.noAttempt ? exercise.noAttempt : 1;
    exercise.noQuiz = 0;
    exercise.noCorrect = 0;
    exercise.noIncorrect = 0;
    exercise.timeSpent = 0;
    exercise.lastQuizId = "";
    exercise.progress = 0;
    exercise.percentage = 0;
    exercise.totalQuiz =
      exercise.exQuizzes && exercise.exQuizzes.length > 0
        ? exercise.exQuizzes.length
        : 0;
    exercise.isHighest = false;

    exercise.isActive = true;
    exercise.isDeleted = false;
    exercise.createdOn = new Date();
    exercise.createdBy = this.userLogin.uid;
    exercise.updatedOn = new Date();
    exercise.author = this.userLogin;
    exercise.visibility = this.visibility;

    return this.exercisesRef.add({ ...exercise }).then((docRef) => {
      exercise.id = docRef.id;
      const syncData = { id: exercise.id, ...exercise };
      this.sync(syncData);

      return exercise;
    });
  }

  updateHighest(id: string, value: boolean): Promise<void> {
    return this.exercisesRef.doc(id).update({
      isHighest: value,
    });
  }

  deleteExerciseUser(id: string): Promise<void> {
    const updatedOn = new Date();
    const updatedBy = this.userLogin.uid;

    return this.exercisesRef.doc(id).update({
      isDeleted: true,
      updatedOn: updatedOn,
      updatedBy: updatedBy,
    });
  }

  //Call function to init ExerciseUser
  initExerciseUser(exerciseId: string) {
    const callable = this.fns.httpsCallable("exercise-createExUser");
    return callable({
      id: exerciseId,
    });
  }

  sync(content) {
    content.createdOn ? delete content.createdOn : null;
    content.updatedOn ? delete content.updatedOn : null;
    content.tags = JSON.stringify(content.tags);
    content.fsId = content.id ? content.id : "";

    if (environment.syncLive) {
      this.http.post(this.baseUrl + "/ExerciseUsers", content).subscribe({
        next: () => {},
        error: (err) => {},
      });
    }
  }
}
